import { useEffect, useEffectEvent, useRef, useState } from 'react'
import {
  Check,
  Columns3,
  Download,
  FileSpreadsheet,
  FileText,
  ListFilter,
  RotateCcw,
  Search,
  SlidersHorizontal,
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { buttonVariants } from '@/ui/button-variants'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/ui/dropdown-menu'
import { Input } from '@/ui/input'

import type { DataGridParameterSchema } from '../lib/types'
import { useGridContext } from './data-grid-context'
import { DataGridFilterControl } from './data-grid-filters'
import { DataGridFilterIcon, keepKeysOutOfMenu } from './data-grid-filter-shared'
import { DataGridSavedViewsSubmenu } from './data-grid-saved-views'

export function DataGridToolbarMenu() {
  const { grid, menuActions } = useGridContext()
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const activeViewLabel = grid.savedViews?.activeView?.name ?? (grid.savedViews ? {
    everything: 'Everything',
    custom: 'Custom view',
    loading: 'Loading views…',
    unavailable: 'Views unavailable',
    saved: 'Custom view',
  }[grid.savedViews.status] : null)

  return (
    <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenuTrigger
        ref={triggerRef}
        aria-label={activeViewLabel ? `Table view: ${activeViewLabel}` : 'Table options'}
        title={activeViewLabel ? `Table view: ${activeViewLabel}` : 'Table options'}
        className={cn(
          buttonVariants({ variant: 'outline', size: 'icon' }),
          'shrink-0',
        )}
      >
        <SlidersHorizontal aria-hidden className="size-3.5" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-52" keepMounted>
        <DataGridParameterItems />
        {menuActions ? (
          <>
            {menuActions}
            <DropdownMenuSeparator />
          </>
        ) : null}
        <DataGridSavedViewsSubmenu
          savedViews={grid.savedViews}
          onOpenDialog={() => setOpen(false)}
          parentMenuTriggerRef={triggerRef}
        />
        <DataGridFiltersSubmenu />
        <DataGridColumnsSubmenu />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function DataGridParameterItems() {
  const { grid } = useGridContext()
  const parameters = Object.values(grid.contract.parameters ?? {})
  if (parameters.length === 0) return null

  return (
    <>
      {parameters.map((parameter) => <DataGridParameterItem key={parameter.id} parameter={parameter} />)}
      <DropdownMenuSeparator />
    </>
  )
}

function DataGridParameterItem({ parameter }: { parameter: DataGridParameterSchema }) {
  const { grid } = useGridContext()
  switch (parameter.type) {
    case 'boolean':
      return (
        <DropdownMenuCheckboxItem
          checked={grid.parameters[parameter.id] === true}
          indicator="checkbox"
          closeOnClick={false}
          onCheckedChange={(checked) => grid.setParameter(parameter.id, checked)}
        >
          {parameter.label}
        </DropdownMenuCheckboxItem>
      )
  }
}

function DataGridFiltersSubmenu() {
  const { grid } = useGridContext()
  const [query, setQuery] = useState('')
  const [openColumnId, setOpenColumnId] = useState<string | null>(null)
  const columns = grid.schema.columns.filter((column) => column.filterable)
  const filtersKey = JSON.stringify(grid.criteria.filter ?? [])
  const normalizedQuery = query.trim().toLocaleLowerCase()
  const visibleColumns = normalizedQuery
    ? columns.filter((column) => [column.label, column.id].some((candidate) =>
        candidate.toLocaleLowerCase().includes(normalizedQuery)))
    : columns
  const activeCount = columns.filter((column) =>
    grid.criteria.filter?.some((filter) => filter.column === column.id)).length
  const refreshOpenCategory = useEffectEvent(() => {
    const column = columns.find((candidate) => candidate.id === openColumnId)
    if (column?.kind === 'category' && column.operators.includes('in')) {
      void grid.loadFacetOptions(column.id)
    }
  })

  useEffect(() => {
    refreshOpenCategory()
  }, [filtersKey, openColumnId])

  return (
    <DropdownMenuSub
      onOpenChange={(next) => {
        if (!next) {
          setQuery('')
          setOpenColumnId(null)
        }
      }}
    >
      <DropdownMenuSubTrigger openOnHover>
        <ListFilter aria-hidden className="size-3.5" />
        <span className="flex-1">Filters</span>
        {activeCount > 0 ? (
          <span className="bg-primary/10 text-primary rounded-full px-1.5 text-[10px] font-semibold tabular-nums">
            {activeCount}
          </span>
        ) : null}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="min-w-56">
        <DropdownMenuItem
          disabled={activeCount === 0}
          closeOnClick={false}
          onClick={() => {
            grid.setFilters([])
            setOpenColumnId(null)
          }}
        >
          <RotateCcw aria-hidden className="size-3.5" />
          Reset filters
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <div className="border-border border-b p-1.5">
          <div className="relative">
            <Search
              aria-hidden
              className="text-muted-foreground pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2"
            />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={keepKeysOutOfMenu}
              placeholder="Find a column…"
              aria-label="Find a filter column"
              className="bg-muted/60 h-8 border-0 pr-2 pl-7 text-xs shadow-none"
            />
          </div>
        </div>
        {columns.length === 0 ? (
          <DropdownMenuItem disabled>No filters available</DropdownMenuItem>
        ) : visibleColumns.length === 0 ? (
          <DropdownMenuItem disabled>No columns match</DropdownMenuItem>
        ) : visibleColumns.map((column) => {
          const active = grid.criteria.filter?.some((filter) =>
            filter.column === column.id) ?? false
          const columnOpen = openColumnId === column.id
          return (
            <DropdownMenuSub
              key={column.id}
              open={columnOpen}
              onOpenChange={(next) => {
                setOpenColumnId((current) => next
                  ? column.id
                  : current === column.id ? null : current)
              }}
            >
              <DropdownMenuSubTrigger
                openOnHover
                onPointerEnter={() => setOpenColumnId(column.id)}
              >
                <DataGridFilterIcon
                  kind={column.kind}
                  className="text-muted-foreground size-3.5"
                />
                <span className="min-w-0 flex-1 truncate">{column.label}</span>
                {active ? <Check aria-label="Active" className="text-primary size-3.5" /> : null}
              </DropdownMenuSubTrigger>
              {columnOpen ? (
                <DataGridFilterControl
                  column={column}
                  presentation="menu"
                  open
                  onBack={() => setOpenColumnId(null)}
                />
              ) : null}
            </DropdownMenuSub>
          )
        })}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  )
}

function DataGridColumnsSubmenu() {
  const { grid, columnChooserColumns } = useGridContext()
  const columns = columnChooserColumns
    ? grid.schema.columns.filter((column) => columnChooserColumns.includes(column.id))
    : grid.schema.columns

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger openOnHover>
        <Columns3 aria-hidden className="size-3.5" />
        Columns
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="max-h-80 min-w-52 overflow-y-auto">
        {columns.map((column) => {
          const checked = grid.selectedColumns.includes(column.id)
          return (
            <DropdownMenuCheckboxItem
              key={column.id}
              checked={checked}
              indicator="checkbox"
              className="text-foreground"
              onCheckedChange={(next) => {
                grid.setSelectedColumns(next
                  ? [...grid.selectedColumns, column.id]
                  : grid.selectedColumns.filter((id) => id !== column.id))
              }}
            >
              {column.label}
            </DropdownMenuCheckboxItem>
          )
        })}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  )
}

export function DataGridExportMenu() {
  const { grid } = useGridContext()

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger className={cn(buttonVariants({ variant: 'outline' }), 'shrink-0')}>
        <Download aria-hidden className="size-3.5" />
        Export
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-48">
        <DropdownMenuItem
          disabled={grid.isExporting}
          closeOnClick={false}
          onClick={() => void grid.exportAs('csv')}
        >
          <FileText aria-hidden className="size-3.5" />
          {grid.exportingFormat === 'csv' ? 'Exporting…' : 'Export CSV'}
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={grid.isExporting}
          closeOnClick={false}
          onClick={() => void grid.exportAs('xlsx')}
        >
          <FileSpreadsheet aria-hidden className="size-3.5" />
          {grid.exportingFormat === 'xlsx' ? 'Exporting…' : 'Export Excel'}
        </DropdownMenuItem>
        {grid.isExporting ? (
          <DropdownMenuItem closeOnClick={false} onClick={grid.cancelExport}>
            Cancel export
          </DropdownMenuItem>
        ) : null}
        {grid.exportError ? (
          <DropdownMenuItem disabled className="text-destructive">
            {grid.exportError.message}
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
